import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from '@pisaka/ui';
import { useLogin, useAuth } from '../useAuth';

type LoginContainerType = {
  onCloseModal: () => void;
  onSwitchToResetPassword?: () => void;
};

export const LoginContainer: FC<LoginContainerType> = ({ onCloseModal, onSwitchToResetPassword }) => {
  const { t } = useTranslation();
  const { isLoadingLogin, login, loginError, setLoginError } = useLogin();
  const {
    email, emailError, isValidEmail,
    password, passwordError, isValidPassword,
    onChangeEmail, onChangePassword,
  } = useAuth();

  const onLogin = async () => {
    try {
      await login(email, password);
      onCloseModal();
      setLoginError(undefined);
    } catch (error) {
      if (error instanceof Error) setLoginError(error.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>{t('auth.emailLabel')}</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => onChangeEmail(e.target.value)}
          placeholder={t('auth.emailPlaceholder')}
        />
        {emailError && <p className="text-sm text-destructive">{emailError}</p>}
      </div>
      <div className="space-y-1">
        <Label>{t('auth.passwordLabel')}</Label>
        <Input
          type="password"
          value={password}
          onChange={(e) => onChangePassword(e.target.value)}
          placeholder={t('auth.passwordPlaceholder')}
        />
        {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
        {onSwitchToResetPassword && (
          <button
            type="button"
            onClick={onSwitchToResetPassword}
            className="text-sm text-blue-500 hover:text-blue-700 underline cursor-pointer"
          >
            {t('auth.forgotPassword')}
          </button>
        )}
      </div>
      {loginError && <p className="text-sm text-destructive">{loginError}</p>}
      <Button
        className="w-full bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground"
        onClick={onLogin}
        disabled={isLoadingLogin || !isValidEmail || !isValidPassword}
      >
        {isLoadingLogin ? '...' : t('auth.loginButton')}
      </Button>
    </div>
  );
};
